import { useRegister } from "@/features/auth/auth.hooks";
import { rootRoute } from "@/routes";
import { validatePassword } from "@/shared/lib/passwordPolicy";
import { CommonHeader } from "@/shared/ui/composites";
import { TextInputField } from "@/shared/ui/composites";
import { Button, Divider, Spinner, VStack } from "@/shared/ui/primitives";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Desktop = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const register = useRegister();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [confirmError, setConfirmError] = useState("");

	const handleSignup = () => {
		const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
		const pwError = validatePassword(password, email);
		const isConfirmValid = password === confirmPassword;
		setEmailError(isEmailValid ? "" : t("auth.emailError"));
		setPasswordError(pwError ? t(pwError) : "");
		setConfirmError(isConfirmValid ? "" : t("auth.passwordConfirmError"));
		if (!isEmailValid || pwError || !isConfirmValid) return;
		register.mutate(
			{ email, password },
			{
				onSuccess: (data) => {
					if (data.requiresVerification !== false) {
						navigate({ to: "/verify", search: { email, redirect: "/login" }, replace: true });
						return;
					}

					navigate({ to: "/", replace: true });
				},
				onError: (error) => {
					if (error instanceof AxiosError && error.response?.status === 409) {
						const code = error.response.data?.code as string | undefined;
						setEmailError(
							t(
								code === "OAUTH_CONFLICT"
									? "auth.emailOAuthConflict"
									: "auth.emailAlreadyRegistered",
							),
						);
					}
				},
			},
		);
	};

	return (
		<VStack className="w-full h-full bg-bg-weak px-[20px] md:px-0">
			<form
				className="flex-col w-full max-w-[480px] rounded-[16px] outline-outline p-[20px] gap-[24px]"
				onSubmit={(e) => {
					e.preventDefault();
					handleSignup();
				}}
			>
				<CommonHeader
					title={t("auth.signup")}
					description={t("auth.signupDescription")}
					onTap={() => navigate({ to: "/login", replace: true })}
				/>
				<Divider className="w-full" />
				<VStack className="w-full gap-[10px]">
					<TextInputField
						label={t("auth.email")}
						placeholder={t("auth.emailHint")}
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							setEmailError("");
						}}
						error={!!emailError}
						errorMessage={emailError}
					/>
					<TextInputField
						label={t("auth.password")}
						placeholder={t("auth.passwordHint")}
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setPasswordError("");
						}}
						type="password"
						error={!!passwordError}
						errorMessage={passwordError}
					/>
					<TextInputField
						label={t("auth.passwordConfirm")}
						placeholder={t("auth.passwordConfirmPlaceholder")}
						value={confirmPassword}
						onChange={(e) => {
							setConfirmPassword(e.target.value);
							setConfirmError("");
						}}
						type="password"
						error={!!confirmError}
						errorMessage={confirmError}
					/>
				</VStack>
				<Button
					variant="primary"
					size="lg"
					className="w-full"
					type="submit"
					disabled={register.isPending}
				>
					{register.isPending ? <Spinner size="sm" /> : t("auth.signup")}
				</Button>
			</form>
		</VStack>
	);
};

export const signupRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/signup",
	component: Desktop,
});
